/**
 * Le vocabulaire du plan d'achat (V1.6-08).
 *
 * Un plan est une **décision figée**. Les mots choisis ici disent cette
 * différence avec la comparaison : celle-ci propose et se recalcule, celui-là
 * a été arrêté par quelqu'un, un jour, sur des données précises.
 *
 * Deux règles héritées de V1.6-07 et non négociées :
 *
 * * un montant absent s'écrit « inconnu », jamais « 0,00 $ ». Un total sans
 *   transport chiffré n'est pas un total ;
 * * aucun total toutes devises confondues. Deux devises font deux lignes.
 *
 * Et une propre à ce ticket : **un plan périmé le dit avant tout le reste.**
 * Le libellé d'un écart nomme ce qui a bougé, pas « des données ont changé ».
 */
import type {
  ComparisonWarning,
  PlanChange,
  PlanChangeKind,
  PlanCurrencyTotal,
  PurchasePlan,
  PurchasePlanSource,
  PurchasePlanStatus,
} from "@/api/types";
import type { BadgeTone } from "@/components/StatusBadge";
import { formatAmount, formatComparisonWarning } from "@/lib/comparison-display";

/** Le mot que l'acceptation d'une couverture partielle porte côté serveur. */
export const PARTIAL_COVERAGE_ACK = "PARTIAL_COVERAGE";

/**
 * Les acceptations de couverture (V1.6-14.4), préfixées côté serveur pour
 * qu'aucune ne puisse se confondre avec un avertissement commercial.
 *
 * Elles portent sur des faits du chantier — le besoin, ce qui est en route —
 * et non sur l'offre. Une phrase distincte le dit, plutôt qu'un « j'accepte : »
 * suivi d'un code que `formatComparisonWarning` ne connaît pas.
 */
const COVERAGE_ACKS: Record<string, string> = {
  COVERAGE_NEED_INCREASED:
    "J'accepte que le besoin ait augmenté et que ce plan ne couvre que sa part d'origine",
  COVERAGE_INCOMING_LATE_KNOWN:
    "J'accepte qu'une commande soit attendue en retard et que j'achète quand même",
};

export function formatPlanStatus(status: PurchasePlanStatus): string {
  switch (status) {
    case "DRAFT":
      return "Brouillon";
    case "APPROVED":
      return "Approuvé";
    case "SUPERSEDED":
      return "Remplacé";
    case "CANCELLED":
      return "Annulé";
    default:
      return status;
  }
}

export function planStatusTone(status: PurchasePlanStatus): BadgeTone {
  switch (status) {
    case "DRAFT":
      return "info";
    case "APPROVED":
      return "available";
    case "SUPERSEDED":
      return "unknown";
    case "CANCELLED":
      return "out";
    default:
      return "unknown";
  }
}

export function formatPlanSource(source: PurchasePlanSource): string {
  switch (source) {
    case "FROM_SCENARIO":
      return "Depuis un scénario";
    case "CUSTOM":
      return "Choix manuel";
    default:
      return source;
  }
}

/**
 * Ce qui a bougé sous le plan, dit comme un acheteur le dirait.
 *
 * `UNEXPLAINED_CHANGE` est le cas honnête : l'empreinte a changé sans qu'aucun
 * écart ne soit nommable. On l'affiche tel quel plutôt que d'inventer une
 * cause — un « le prix a changé » faux serait pire que « quelque chose a
 * changé », vrai.
 */
export function formatPlanChangeKind(kind: PlanChangeKind): string {
  switch (kind) {
    case "PRICE_CHANGED":
      return "Prix modifié";
    case "FREIGHT_CHANGED":
      return "Transport modifié";
    case "AVAILABILITY_CHANGED":
      return "Disponibilité modifiée";
    case "LEAD_TIME_CHANGED":
      return "Délai modifié";
    case "PACKAGING_CHANGED":
      return "Conditionnement modifié";
    case "MOQ_CHANGED":
      return "Minimum de commande modifié";
    case "ORDER_MULTIPLE_CHANGED":
      return "Multiple de commande modifié";
    case "CURRENCY_CHANGED":
      return "Devise modifiée";
    case "RESPONSE_CHANGED":
      return "Réponse du fournisseur modifiée";
    case "QUOTE_REVISION_CHANGED":
      return "Soumission révisée";
    case "QUOTE_NO_LONGER_ELIGIBLE":
      return "Soumission plus retenable";
    case "REQUIREMENT_CHANGED":
      return "Besoin modifié";
    case "SUBSTITUTE_APPROVAL_CHANGED":
      return "Équivalence du substitut modifiée";
    case "NEW_QUOTE_AVAILABLE":
      return "Nouvelle soumission validée depuis";
    case "UNEXPLAINED_CHANGE":
      return "Changement non identifié";
    default:
      return kind;
  }
}

/**
 * Un écart tenant sur une ligne : « Prix modifié — 12,50 → 13,75 ».
 *
 * Les deux côtés sont affichés parce que « le prix a changé » ne permet aucune
 * décision, alors que « il a monté de dix pour cent » en permet une.
 */
export function formatPlanChange(change: PlanChange): string {
  const head = `${formatPlanChangeKind(change.kind)} — ${change.label}`;
  if (change.before === null && change.after === null) return head;
  const before = change.before ?? "inconnu";
  const after = change.after ?? "inconnu";
  return `${head} : ${before} → ${after}`;
}

/**
 * Pourquoi la couverture d'aujourd'hui refuse ce plan (V1.6-14.4).
 *
 * Chaque phrase dit **ce qu'il faut faire**, pas seulement ce qui ne va pas :
 * « le besoin a changé » laisse l'utilisateur devant un bouton gris, « recalculez
 * le plan » lui rend la main.
 */
export function formatCoverageBlocker(code: string): string {
  switch (code) {
    case "COVERAGE_EXCEEDS_CURRENT_NEED":
      return "Ce plan achèterait plus que ce dont le chantier a encore besoin. Recalculez-le.";
    case "NEED_REMOVED":
      return "Le besoin que ce plan couvre a disparu du chantier. Recalculez-le.";
    case "REQUIREMENT_SOURCE_MISSING":
      return "Le besoin d'origine d'une ligne n'existe plus. Recalculez le plan.";
    case "INCOMPARABLE_COVERAGE_UNITS":
      return "L'unité d'une ligne ne se compare plus à celle du besoin. Reprenez la demande de prix.";
    case "SUPPLIER_INACTIVE":
      return "Un fournisseur retenu n'est plus actif. Reprenez le plan chez un autre.";
    default:
      return "La couverture a changé depuis la construction du plan. Recalculez-le.";
  }
}

/**
 * Ce qu'un approbateur accepte en cochant. Formulé à la première personne :
 * on ne coche pas un code, on prend une responsabilité.
 */
export function formatAcknowledgement(warning: string): string {
  if (warning === PARTIAL_COVERAGE_ACK) {
    return "J'accepte que ce plan ne couvre pas tout le besoin";
  }
  const coverage = COVERAGE_ACKS[warning];
  if (coverage) return coverage;
  return `J'accepte : ${formatComparisonWarning(warning as ComparisonWarning)}`;
}

/**
 * Un total de devise, ou la raison de son absence.
 *
 * L'absence a ici **une seule** cause possible, et elle est nommable : une
 * soumission retenue n'a pas chiffré son transport. On la dit.
 */
export function formatPlanTotal(total: PlanCurrencyTotal, currency: string): string {
  if (total.total !== null) return formatAmount(total.total, currency);
  return `Marchandise ${formatAmount(total.merchandise, currency)} — transport non chiffré`;
}

/**
 * Les totaux d'un plan, une entrée par devise, dans un ordre stable.
 *
 * Rend les paires plutôt que les seules devises : un appelant qui garde la clé
 * puis va rechercher la valeur retombe sur un `undefined` que rien ne signale,
 * et affiche alors un total vide sans le savoir.
 */
export function planTotals(plan: PurchasePlan): [string, PlanCurrencyTotal][] {
  return Object.entries(plan.totals_by_currency).sort(([a], [b]) => a.localeCompare(b));
}

/**
 * Le nom d'une révision : `PLAN-000012 rév. 3`.
 *
 * Le code est **stable** d'une révision à l'autre — c'est la même décision qui
 * évolue. Les afficher séparément évite qu'on croie à deux plans distincts.
 */
export function formatPlanCode(plan: PurchasePlan): string {
  return plan.revision > 1 ? `${plan.code} rév. ${plan.revision}` : plan.code;
}

/**
 * Pourquoi l'approbation est fermée, ou `null` si elle est ouverte.
 *
 * L'écran ne décide pas : `can_approve` vient du serveur. Cette fonction ne
 * fait que **traduire** un refus déjà pris, pour qu'un bouton grisé s'explique
 * au lieu de laisser deviner.
 */
export function whyCannotApprove(detail: {
  plan: PurchasePlan;
  staleness: { stale: boolean };
  blocking_warnings: string[];
  coverage_blockers: string[];
  can_approve: boolean;
}): string | null {
  if (detail.can_approve) return null;
  if (detail.plan.status !== "DRAFT") {
    return `Un plan ${formatPlanStatus(detail.plan.status).toLowerCase()} ne s'approuve pas.`;
  }
  if (detail.staleness.stale) {
    return "Les données ont changé depuis la construction du plan. Rafraîchir, relire, puis approuver.";
  }
  if (detail.blocking_warnings.length > 0) {
    return "Ce plan retient une offre qu'aucune acceptation ne peut lever.";
  }
  // La couverture après le commercial, parce que les deux peuvent être vrais et
  // que la péremption des prix se corrige par le même geste — reconstruire.
  const [blocker] = detail.coverage_blockers;
  if (blocker) return formatCoverageBlocker(blocker);
  if (detail.plan.line_count === 0) return "Ce plan ne retient aucune ligne.";
  return "L'approbation est fermée.";
}
